// Stable anonymous visitor id.
//
// The server's per-request visitor_hash is a daily-rotating HMAC of IP+UserAgent,
// so it can't recognize a returning visitor across days (every day they look new,
// inflating "unique visitors" and deflating the funnel's signup conversion rate).
// This id fixes that: a random, opaque value persisted in localStorage so the
// SAME browser counts as ONE visitor over time, and so a visitor who later signs
// up can be tied back to their pre-signup visits.
//
// Privacy: it is an anonymous random token — no PII. The server never stores the
// raw value, only a keyed hash of it. It MUST persist across login/logout —
// clearing it would orphan the identity and make the same person look like a new
// visitor, so logout flows deliberately leave it untouched.
const VISITOR_ID_KEY = 'shader_visitor_id';

/**
 * Returns this browser's stable visitor id, generating and persisting one on
 * first call. Returns null when storage is unavailable (e.g. Safari private
 * mode throwing on access) — callers then simply omit it and the server falls
 * back to its daily IP+UA hash. Never throws; analytics is best-effort.
 */
export function getVisitorId(): string | null {
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}
